import { apiClient } from './apiClient';

export interface TenantSummaryDto {
  tenantId: string;
  displayName: string;
  isActive: boolean;
  seatLimit: number;
  contractStartDate: string;
  contractEndDate: string;
  userCount: number;
}

export interface TenantDetailDto {
  tenantId: string;
  displayName: string;
  storageAccountName: string;
  sftpUsername: string;
  timeZoneId: string;
  seatLimit: number;
  dailyManualUploadLimit: number;
  dailyBatchLimit: number;
  maxAudioDurationMinutes: number;
  isActive: boolean;
  contractStartDate: string;
  contractEndDate: string;
  createdDate: string;
  modifiedDate: string | null;
  maskedDatabaseConnection: string;
  maskedStorageConnection: string;
}

export interface UpdateTenantRequest {
  displayName: string;
  sftpUsername: string;
  timeZoneId: string;
  seatLimit: number;
  dailyManualUploadLimit: number;
  dailyBatchLimit: number;
  maxAudioDurationMinutes: number;
  isActive: boolean;
  contractStartDate: string;
  contractEndDate: string;
}

export interface TenantUserDto {
  userId: string;
  tenantId: string;
  displayName: string;
  email: string;
  role: string;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  operatorAlias: string;
  /**
   * Team this user is scoped to, or null for agency-wide.
   *
   * Decides which recordings the user can open, so the roster reads it back rather than only
   * writing it — a Team column that always rendered "Agency-wide" could not answer "which team is
   * this supervisor scoped to", and made every successful save look like it had reverted.
   */
  teamKey: string | null;
  /**
   * Where `email` came from: 'SignIn' (asserted by Entra on a real authentication) or
   * 'Administrative' (typed on this roster by GovIQ staff).
   *
   * Not cosmetic: only a sign-in-verified address is accepted as a scheduled-digest recipient
   * without its domain being allow-listed, so this is the answer to "why is this person not
   * receiving digests".
   */
  emailSource: string;
  /** When `email` was last confirmed by a sign-in. Null when never confirmed. */
  emailVerifiedAt: string | null;
}

export interface AddUserToTenantRequest {
  userId: string;
  role: string;
  displayName?: string;
  email?: string;
  operatorAlias?: string;
}

export interface CreateTenantRequest {
  tenantId: string;
  displayName: string;
  seatLimit: number;
  dailyManualUploadLimit: number;
  dailyBatchLimit: number;
  maxAudioDurationMinutes: number;
  timeZoneId: string;
  contractStartDate: string;
  contractEndDate: string;
}

class TenantConfigurationService {
  async getAllTenants(): Promise<TenantSummaryDto[]> {
    const response = await apiClient.get('/api/tenantconfiguration/tenants');
    return response.data;
  }

  async getTenantDetail(tenantId: string): Promise<TenantDetailDto> {
    const response = await apiClient.get(`/api/tenantconfiguration/tenants/${tenantId}`);
    return response.data;
  }

  async updateTenant(tenantId: string, request: UpdateTenantRequest): Promise<void> {
    await apiClient.put(`/api/tenantconfiguration/tenants/${tenantId}`, request);
  }

  async createTenant(request: CreateTenantRequest): Promise<void> {
    await apiClient.post('/api/tenantconfiguration/tenants', request);
  }

  async getTenantUsers(tenantId: string): Promise<TenantUserDto[]> {
    const response = await apiClient.get(`/api/tenantconfiguration/tenants/${tenantId}/users`);
    return response.data;
  }

  async updateUserRole(userId: string, tenantId: string, role: string): Promise<void> {
    await apiClient.put(`/api/tenantconfiguration/users/${userId}/${tenantId}/role`, { role });
  }

  async updateUserActive(userId: string, tenantId: string, isActive: boolean): Promise<void> {
    await apiClient.put(`/api/tenantconfiguration/users/${userId}/${tenantId}/active`, { isActive });
  }

  async updateOperatorAlias(userId: string, tenantId: string, operatorAlias: string): Promise<void> {
    await apiClient.put(`/api/tenantconfiguration/users/${userId}/${tenantId}/operator-alias`, { operatorAlias });
  }

  async purgeDemoTenantData(): Promise<{ success: boolean; callsDeleted: number; blobsDeleted: number; error?: string }> {
    const response = await apiClient.delete('/api/tenantconfiguration/tenants/demo/data');
    return response.data;
  }

  async removeUserFromTenant(tenantId: string, userId: string): Promise<void> {
    await apiClient.delete(`/api/tenantconfiguration/tenants/${tenantId}/users/${userId}`);
  }

  async addUserToTenant(tenantId: string, request: AddUserToTenantRequest): Promise<{ message: string }> {
    const response = await apiClient.post(`/api/tenantconfiguration/tenants/${tenantId}/users`, request);
    return response.data;
  }

  async seedDemoUser(userId: string, displayName: string, email: string): Promise<{ message: string }> {
    const response = await apiClient.post('/api/tenantconfiguration/tenants/demo/users/seed', {
      userId,
      displayName,
      email,
    });
    return response.data;
  }
}

export const tenantConfigurationService = new TenantConfigurationService();
